import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import ExportButton from "../components/admin/ExportButton";
import { downloadBlob } from "../lib/downloadBlob";
import { exportName, exportPath } from "../lib/exportFile";
import {
  LogOut, RefreshCcw, Menu, MoreHorizontal, Trash2, Gift, HelpCircle,
} from "lucide-react";
import GroupsManager from "../components/admin/GroupsManager";
import TrainersTab from "../components/admin/TrainersTab";
import PortalTab from "../components/admin/PortalTab";
import { TAB_TITLES, VALID_TABS, DEFAULT_TAB } from "../lib/adminTabs";
import WebinarsManager from "../components/admin/WebinarsManager";
import AdminUsers from "../components/admin/AdminUsers";
import AuditLog from "../components/admin/AuditLog";
import SecurityTab from "../components/admin/SecurityTab";
import ImpersonationBanner from "../components/admin/ImpersonationBanner";
import LeadsTab from "../components/admin/LeadsTab";
import PurchasesTab from "../components/admin/PurchasesTab";
import CourseSettings from "../components/admin/CourseSettings";
import EnrollmentsTab from "../components/admin/EnrollmentsTab";
import ContractsTab from "../components/admin/ContractsTab";
import MarketingConsentsTab from "../components/admin/MarketingConsentsTab";
import CertificatesTab from "../components/admin/CertificatesTab";
import SignatureTab from "../components/admin/SignatureTab";
import ReferralsTab from "../components/admin/ReferralsTab";
import TicketsTab from "../components/admin/tickets/TicketsTab";
import ReportButton from "../components/admin/tickets/ReportButton";
import GuideTab from "../components/admin/guide/GuideTab";
import { ARTICLE_PARAM, articlesForScreen, markSeen, readLastSeen, today, unseenNews } from "../lib/guide";
import PrelaunchCleanupDialog from "../components/admin/PrelaunchCleanupDialog";
import AnalyticsCards from "../components/admin/AnalyticsCards";
import AnalyticsTab from "../components/admin/AnalyticsTab";
import AdminSidebar from "../components/admin/AdminSidebar";
import { roleLabel } from "../lib/roleLabels";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const FPED_LOGO = "/assets/logo-fped-color.svg";


const SIDEBAR_LS_KEY = "fped_admin_sidebar_collapsed";

// Profile dropdown role display (Faza 3).
const ROLE_BADGE_COLORS = {
  owner: "bg-[#965EA7]/15 text-[#7A4986]",
  admin_fped: "bg-[#3F8EBA]/15 text-[#1f5d80]",
  admin: "bg-[#3F8EBA]/15 text-[#1f5d80]",
  partner_operator: "bg-[#519872]/15 text-[#2F6B4E]",
  viewer: "bg-[#6B7280]/15 text-[#373A45]",
};

export default function AdminDashboard() {
  const [me, setMe] = useState(null);
  const navigate = useNavigate();
  // M904 — URL is the source of truth for the active section. `tab` is
  // derived from the `:tab` route param; navigating between tabs simply
  // pushes a new entry to the browser history. `setTab` is now a thin
  // wrapper that delegates to `navigate("/admin/<slug>")` so refresh,
  // back/forward and deep linking all keep the section in sync.
  const { tab: urlTab } = useParams();
  const tab = VALID_TABS.has(urlTab) ? urlTab : DEFAULT_TAB;
  const setTab = useCallback(
    (next) => {
      if (next === tab) return;
      navigate(`/admin/${next}`);
    },
    [navigate, tab],
  );
  const [showPrelaunchCleanup, setShowPrelaunchCleanup] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_LS_KEY) === "1"; } catch { return false; }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // When a lead is converted, we hop to Enrollments and auto-open the new drawer.
  const [pendingEnrollmentId, setPendingEnrollmentId] = useState(null);
  const [referralStats, setReferralStats] = useState({ eligible: 0 });
  // Tickets: for the owner the unread ones, for everybody else theirs to check.
  const [ticketBadge, setTicketBadge] = useState(0);
  // The guide (19.10.2026): its table of contents, for the tab, the (?) and "Ce e nou".
  const [guide, setGuide] = useState(null);
  const [guideSeen, setGuideSeen] = useState(() => readLastSeen());

  // M850 — partners can mutate leads/enrollments assigned to them. The
  // backend enforces scope via `ensure_doc_access` per route. Destructive
  // actions (delete, reassign, restore, settings) stay gated by `isOwner`.
  const canMutate = me && (me.role === "owner" || me.role === "admin_fped" || me.role === "admin" || me.role === "partner_operator");
  const isOwner = me && me.role === "owner";
  const isAdmin = me && (me.role === "admin_fped" || me.role === "admin");
  const isPartner = me && me.role === "partner_operator";
  // Tabs visible to both owner & admin (admin sees them in read-only mode).
  const readOnlyTabsVisible = isOwner || isAdmin;

  const toggleSidebar = () => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(SIDEBAR_LS_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const loadTicketBadge = useCallback(async () => {
    try {
      const r = await api.get("/admin/tickets/badge");
      setTicketBadge(r.data?.count || 0);
    } catch { /* non-blocking */ }
  }, []);

  const load = useCallback(async () => {
    try {
      // Resolve auth first; subsequent admin pings depend on a valid session.
      const m = await api.get("/auth/me");
      setMe(m.data);
      try {
        const ref = await api.get("/admin/referrals/pending-count");
        setReferralStats(ref.data || { eligible: 0, disputed: 0, approved: 0 });
      } catch { /* non-blocking */ }
      loadTicketBadge();
    } catch {
      navigate("/admin/login");
    }
  }, [navigate, loadTicketBadge]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!readOnlyTabsVisible) return undefined;
    let live = true;
    api.get("/admin/guide").then((r) => { if (live) setGuide(r.data); }).catch(() => { /* non-blocking */ });
    return () => { live = false; };
  }, [readOnlyTabsVisible]);

  useEffect(() => {
    if (tab !== "ghid" || !guide) return;
    const day = today();
    markSeen(day);
    setGuideSeen(day);
  }, [tab, guide]);

  // Auth Faza 2 (M850 + M852): partner_operator now sees leads + enrollments +
  // their own groups. Other tabs (analytics, purchases, settings, users) remain
  // owner/admin only — redirect if they somehow land there. We use `replace`
  // so the forbidden URL doesn't sit in the browser history.
  useEffect(() => {
    if (isPartner && !["leads", "enrollments", "groups", "tickets"].includes(tab)) {
      navigate(`/admin/${DEFAULT_TAB}`, { replace: true });
    }
  }, [isPartner, tab, navigate]);

  // M922 — Security module is owner-only. Even when a non-owner types
  // /admin/security directly, redirect them home with a clear toast so they
  // understand WHY the URL didn't load.
  useEffect(() => {
    if (!me) return;
    if (tab === "security" && !isOwner) {
      toast.error("Nu ai permisiunea de a accesa această secțiune.");
      navigate(`/admin/${DEFAULT_TAB}`, { replace: true });
    }
  }, [me, tab, isOwner, navigate]);

  // Default global export — tab-specific exports live inside Leads/Enrollments toolbars.
  const exportCsv = (format) => downloadBlob(exportPath("/admin/leads/export", null, format), exportName("leads", format));

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    navigate("/admin/login");
  };

  const referralBadge = referralStats.eligible || 0;
  const guideBadge = tab === "ghid" ? 0 : unseenNews(guide, guideSeen).length;
  const helpFor = articlesForScreen(guide, `/admin/${tab}`)[0];

  return (
    // M912 — Admin Dashboard contains lead PII. Marked `clarity-mask` as
    // defence-in-depth on top of the route gate in `lib/tracking.js`.
    <div className="min-h-screen bg-[#F5F6F6] flex flex-col clarity-mask" data-clarity-mask="true" data-testid="admin-dashboard">
      {/* Auth Faza 5 — sticky impersonation banner. Renders nothing when
          /auth/me reports a normal session, so prod overhead is zero. */}
      <ImpersonationBanner me={me} />
      {/* ── Full-width topbar ───────────────────────────────────────────
          Sits ABOVE the sidebar so the brand logo never gets clipped by
          the collapse toggle. Logo + title on the left, section title
          centred-ish, user controls on the right. */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-50 h-14 flex items-center px-3 md:px-6 gap-3" data-testid="admin-topbar">
        <button
          onClick={() => setMobileNavOpen(true)}
          className="lg:hidden text-[#373A45] rounded-md p-1.5 hover:bg-[#F5F6F6]"
          aria-label="Deschide meniul"
          data-testid="admin-mobile-nav-toggle"
        >
          <Menu size={18} />
        </button>
        <div className="flex items-center gap-2 min-w-0" data-testid="topbar-brand">
          <img src={FPED_LOGO} alt="FPED" className="h-8 w-auto object-contain shrink-0" />
          <span className="hidden md:inline font-display text-sm font-bold text-[#373A45] tracking-wide whitespace-nowrap">· CRM</span>
        </div>
        <div className="h-6 w-px bg-[#E5E7EB] hidden md:block" />
        <div className="font-display text-sm sm:text-base font-semibold text-[#373A45] truncate" data-testid="admin-section-title">
          {TAB_TITLES[tab] || ""}
        </div>
        <div className="flex-1" />
        <div className="hidden sm:flex items-center gap-2">
          {me && <RoleBadge role={me.role} />}
          <span className="text-xs text-[#6B7280] hidden lg:inline" data-testid="admin-current-email">{me?.email}</span>
        </div>
        {helpFor && (
          <button onClick={() => navigate(`/admin/ghid?${ARTICLE_PARAM}=${encodeURIComponent(helpFor.id)}`)}
            className="rounded-full p-1.5 border border-[#E5E7EB] hover:bg-[#F5F6F6] text-[#373A45]"
            aria-label={`Ajutor: ${helpFor.title}`} title={`Ajutor: ${helpFor.title}`} data-testid="admin-help">
            <HelpCircle size={16} />
          </button>
        )}
        {me && <ReportButton tab={tab} onCreated={loadTicketBadge} />}
        <ExportButton
          onExport={exportCsv}
          className="text-sm rounded-full px-3 py-1.5 bg-[#373A45] text-white hover:bg-[#1F2128] inline-flex items-center gap-1.5"
          testid="admin-export-csv"
          title="Export rapid: toate leadurile"
        />
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              className="rounded-full p-1.5 border border-[#E5E7EB] hover:bg-[#F5F6F6] text-[#373A45]"
              data-testid="admin-more-menu"
              aria-label="Acțiuni avansate"
            >
              <MoreHorizontal size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="space-y-0.5">
                <div className="text-sm font-semibold text-[#373A45]" data-testid="profile-email">{me?.email}</div>
                <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider">
                  <span
                    className={`inline-block rounded-full px-1.5 py-px ${ROLE_BADGE_COLORS[me?.role] || "bg-[#EDEEF0] text-[#373A45]"}`}
                    data-testid="profile-role-badge"
                  >
                    {roleLabel(me?.role) || "—"}
                  </span>
                  {me?.assigned_org && (
                    <span className="rounded-full bg-[#EDEEF0] text-[#373A45] px-1.5 py-px" data-testid="profile-org">
                      {me.assigned_org}
                    </span>
                  )}
                  {me?.auth_channel && (
                    <span className="rounded-full bg-[#EDEEF0] text-[#6B7280] px-1.5 py-px" data-testid="profile-auth-channel">
                      {me.auth_channel === "google" ? "Google" : me.auth_channel === "magic_link" ? "Magic Link" : me.auth_channel}
                    </span>
                  )}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Acțiuni rapide</DropdownMenuLabel>
            <DropdownMenuItem onClick={load} data-testid="admin-action-reload">
              <RefreshCcw size={14} className="mr-2" /> Reîncarcă datele
            </DropdownMenuItem>
            {isOwner && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Întreținere (owner)</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => setShowPrelaunchCleanup(true)}
                  data-testid="admin-action-prelaunch-cleanup"
                  className="text-[#9b1f2c] focus:text-[#9b1f2c]"
                >
                  <Trash2 size={14} className="mr-2" /> Reset date prelaunch
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="admin-logout">
              <LogOut size={14} className="mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* ── Body: sidebar (under topbar) + main content ──────────────── */}
      <AdminSidebar
        activeTab={tab}
        onSelect={setTab}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebar}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
        isOwner={isOwner}
        readOnlyTabsVisible={readOnlyTabsVisible}
        isPartner={isPartner}
        badges={{ referralBadge, ticketBadge, guideBadge }}
        brandLogo={FPED_LOGO}
      />

      <div className={`flex-1 transition-[margin] duration-200 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <main className="px-4 md:px-8 py-6 max-w-[1600px] mx-auto">
          {/* CRM analytics snapshot — server-cached 60s. Wait for `me` to avoid
              a stray 401 on initial render before auth state is hydrated.
              Hidden for partner_operator (no access to global analytics). */}
          {/* The CRM figures say nothing about the portal, which has its own, about tickets or the guide. */}
          {me && !isPartner && !["portal", "tickets", "ghid"].includes(tab) && <AnalyticsCards />}

          {/* Referrals: somebody reached the number of paid referrals, so a
              benefit is due. Only for who can open the referrals page. */}
          {!isPartner && referralStats.eligible > 0 && (
            <button
              onClick={() => setTab("referrals")}
              className="mt-4 w-full text-left bg-gradient-to-r from-[#519872]/[0.08] to-[#965EA7]/[0.08] border border-[#519872]/30 rounded-xl px-4 py-3 flex items-center gap-3 hover:from-[#519872]/[0.14] hover:to-[#965EA7]/[0.14] transition-colors"
              data-testid="referral-stat-card"
            >
              <div className="rounded-full bg-[#519872] text-white p-2 shrink-0">
                <Gift size={16} />
              </div>
              <div className="flex-1 text-sm">
                <span className="font-semibold text-[#373A45]">
                  {referralStats.eligible === 1
                    ? "O persoană a strâns recomandările necesare: beneficiul e de dat"
                    : `${referralStats.eligible} persoane au strâns recomandările necesare: beneficiul e de dat`}
                </span>
                <span className="text-[#6B7280] ml-2">→ Recomandări</span>
              </div>
            </button>
          )}

          {/* Content */}
          <div className="mt-5">
            {tab === "leads" && (
              <LeadsTab
                canMutate={canMutate}
                isOwner={isOwner}
                currentEmail={me?.email}
                currentRole={me?.role}
                onLeadConverted={(enrollmentId) => {
                  setPendingEnrollmentId(enrollmentId);
                  setTab("enrollments");
                }}
              />
            )}

            {tab === "enrollments" && (
              <EnrollmentsTab
                canMutate={canMutate}
                isOwner={isOwner}
                currentRole={me?.role}
                initialDrawerId={pendingEnrollmentId}
                onInitialDrawerConsumed={() => setPendingEnrollmentId(null)}
              />
            )}

            {tab === "contracts" && readOnlyTabsVisible && (
              <ContractsTab
                isOwner={isOwner}
                onOpenEnrollment={(enrollmentId) => {
                  setPendingEnrollmentId(enrollmentId);
                  setTab("enrollments");
                }}
              />
            )}

            {tab === "referrals" && !isPartner && (
              <ReferralsTab canMutate={canMutate} isOwner={isOwner} />
            )}

            {tab === "purchases" && !isPartner && (
              <PurchasesTab canMutate={canMutate} isOwner={isOwner} currentEmail={me?.email} />
            )}

            {tab === "groups" && <GroupsManager canMutate={canMutate} currentRole={me?.role} />}
            {tab === "trainers" && <TrainersTab canMutate={canMutate} canPreview={readOnlyTabsVisible} />}
            {tab === "portal" && readOnlyTabsVisible && <PortalTab />}
            {tab === "webinars" && !isPartner && <WebinarsManager canMutate={canMutate} currentRole={me?.role} />}
            {tab === "certificates" && !isPartner && <CertificatesTab isOwner={isOwner} canManage={readOnlyTabsVisible} />}
            {tab === "analytics" && !isPartner && <AnalyticsTab />}
            {tab === "consents" && readOnlyTabsVisible && <MarketingConsentsTab />}
            {tab === "settings" && readOnlyTabsVisible && <CourseSettings canMutate={isOwner} />}
            {tab === "signature" && readOnlyTabsVisible && <SignatureTab isOwner={isOwner} />}
            {tab === "users" && readOnlyTabsVisible && <AdminUsers isOwner={isOwner} currentEmail={me?.email} currentRole={me?.role} />}
            {tab === "security" && isOwner && <SecurityTab currentRole={me?.role} currentEmail={me?.email} />}
            {tab === "audit" && isOwner && <AuditLog />}
            {tab === "ghid" && readOnlyTabsVisible && <GuideTab index={guide} onTicket={loadTicketBadge} />}
            {tab === "tickets" && me && (
              <TicketsTab readsAll={readOnlyTabsVisible} isOwner={isOwner} me={(me.email || "").toLowerCase()}
                onChanged={loadTicketBadge} />
            )}
          </div>
        </main>
      </div>

      {showPrelaunchCleanup && (
        <PrelaunchCleanupDialog
          onClose={() => setShowPrelaunchCleanup(false)}
          onDone={() => { setShowPrelaunchCleanup(false); load(); }}
        />
      )}
    </div>
  );
}


function RoleBadge({ role }) {
  const map = {
    owner: "bg-[#965EA7]/15 text-[#7A4986]",
    admin: "bg-[#3F8EBA]/15 text-[#1f5d80]",
    admin_fped: "bg-[#3F8EBA]/15 text-[#1f5d80]",
    viewer: "bg-[#6B7280]/15 text-[#373A45]",
  };
  return (
    <span className={`hidden md:inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest ${map[role] || ""}`} data-testid="admin-role-badge">
      {roleLabel(role)}
    </span>
  );
}
